import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { Grid, Typography, Box } from '@mui/material';
import moment from 'moment';

import FHAntdDateTimePicker from '../../components/base/FHAntdDateTimePicker';
import FHAutocomplete from '../../components/base/FHAutocomplete';
import FHButton from '../../components/base/FHButton';
import { clientActions, cohortActions } from '../../store';
import { FORTA_BLUE, FORTA_WHITE } from '../../utils/colors';
import { CohortAssignmentMode } from '../../constants';
import { ClinicalStatus } from '../constants/general';

import type { RootState } from '../../store';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { IClient, IClientAssignment, IOption } from '../../utils/types';

interface IClientAssignmentRowProps {
  clientAssignment: IClientAssignment;
  isAdding: boolean;
  isEditing: boolean;
  isDisabled: boolean;
  showActions?: boolean;
  onCancel: () => void;
  onEdit: () => void;
  onSave: (editableAssignment: IClientAssignment) => void;
  onDelete: (editableAssignment: IClientAssignment) => void;
  mode: CohortAssignmentMode;
}
const CohortAssignmentRow = ({
  clientAssignment,
  isAdding,
  isDisabled,
  isEditing,
  showActions = true,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  mode,
}: IClientAssignmentRowProps) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { list: clientList } = useSelector((state: RootState) => state.clients);
  const { list: cohortList } = useSelector((state: RootState) => state.cohorts);

  const [selectedClient, setSelectedClient] = useState<IOption | null>();
  const [selectedCohort, setSelectedCohort] = useState<IOption | null>();
  const [editableAssignment, setEditableAssignment] = useState({ ...clientAssignment });

  useEffect(() => {
    setEditableAssignment({ ...clientAssignment });

    //Get list of clients only when adding from Cohort page
    if (mode === CohortAssignmentMode.Cohort && isAdding && !clientList.length) {
      dispatch(clientActions.getList({ getAccessTokenSilently }));
    }

    if (mode === CohortAssignmentMode.Client && isAdding && !cohortList.length) {
      dispatch(cohortActions.getList({ getAccessTokenSilently }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientAssignment]);

  const handleFieldChange = (field: string, value: any) => {
    setEditableAssignment((prevAssignment: IClientAssignment) => ({
      ...prevAssignment,
      [field]: value,
    }));
  };

  const activeClients = clientList.filter(
    (client: IClient) => client.clinical_status === ClinicalStatus.CLINICAL,
  );

  const validateRow = () => {
    if (!selectedCohort && mode === CohortAssignmentMode.Client) {
      toast.error('Please select a cohort');
      return false;
    }
    if (!selectedClient && mode === CohortAssignmentMode.Cohort) {
      toast.error('Please select a client');
      return false;
    }
    if (!editableAssignment.date_start) {
      toast.error('Please select date start');
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (isAdding) {
      if (validateRow()) {
        onSave(editableAssignment);
      }
      setSelectedClient(null);
      setSelectedCohort(null);
      return;
    }
    if (isEditing) {
      if (!editableAssignment.date_start) {
        toast.error('Please select date start');
        return;
      }
      onSave(editableAssignment);
      return;
    }
  };

  const handleCancel = () => {
    if (mode === 'cohort') {
      setSelectedClient(null);
    } else {
      setSelectedCohort(null);
    }
    setEditableAssignment({ ...clientAssignment });
    onCancel();
  };

  const handleDelete = () => {
    onDelete(editableAssignment);
  };

  return (
    <>
      <Grid
        container
        p={1.5}
        sx={{ backgroundColor: FORTA_WHITE }}
        mb={2}
        borderRadius="10px"
        direction="row"
        alignItems="center"
      >
        <Grid item xs={3} mr={2}>
          {isAdding ? (
            <FHAutocomplete
              disableClearable
              inputHeight={40}
              sx={{ width: '100%', boxShadow: 'none' }}
              coloredBackground
              placeholder={`Select ${mode === CohortAssignmentMode.Cohort ? 'client' : 'cohort'}`}
              options={
                mode === CohortAssignmentMode.Cohort
                  ? activeClients.map((client: IClient) => ({
                      id: client.id,
                      label: `${client.first_name} ${client.last_name}`,
                    }))
                  : cohortList.map((cohort) => ({
                      id: cohort.id,
                      label: cohort.name,
                    }))
              }
              renderOption={(props: any, option: IOption) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
              value={
                mode === CohortAssignmentMode.Cohort
                  ? selectedClient
                    ? selectedClient
                    : clientAssignment?.client?.id
                      ? setSelectedClient({
                          id: clientAssignment?.client?.id,
                          label: `${clientAssignment?.client?.first_name} ${clientAssignment?.client?.last_name}`,
                        })
                      : null
                  : selectedCohort
                    ? selectedCohort
                    : clientAssignment?.cohort?.id
                      ? setSelectedCohort({
                          id: clientAssignment?.cohort?.id,
                          label: clientAssignment?.cohort?.name,
                        })
                      : null
              }
              onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                if (mode === CohortAssignmentMode.Cohort) {
                  setSelectedClient(newValue);
                  handleFieldChange('client', newValue ? newValue : '');
                } else {
                  setSelectedCohort(newValue);
                  handleFieldChange('cohort', newValue ? newValue : '');
                }
              }}
            />
          ) : (
            <Typography textAlign={'left'}>
              {mode === CohortAssignmentMode.Cohort ? (
                <React.Fragment key={clientAssignment?.client.id}>
                  <Link to={'/admin/client-details-page/' + clientAssignment?.client?.id}>
                    {`${clientAssignment?.client?.first_name} ${clientAssignment?.client?.last_name}`}
                  </Link>
                </React.Fragment>
              ) : (
                <React.Fragment key={clientAssignment?.cohort?.id}>
                  <Link to={'/admin/cohort/' + clientAssignment?.cohort?.id}>
                    {`${clientAssignment?.cohort?.name}`}
                  </Link>
                </React.Fragment>
              )}
            </Typography>
          )}
        </Grid>

        <Grid item xs={2} mr={2}>
          {isAdding || isEditing ? (
            <FHAntdDateTimePicker
              format="MM/DD/YYYY"
              inputHeight={40}
              sx={{ width: '100%', boxShadow: 'none' }}
              allowClear={false}
              coloredBackground
              value={
                editableAssignment.date_start
                  ? moment(editableAssignment.date_start, 'YYYY-MM-DD')
                  : null
              }
              onSelect={(value: Moment) =>
                handleFieldChange('date_start', value.format('YYYY-MM-DD'))
              }
              onChange={(value: Moment | null) =>
                handleFieldChange('date_start', value ? value.format('YYYY-MM-DD') : null)
              }
            />
          ) : (
            <Typography>
              {clientAssignment?.date_start
                ? moment(clientAssignment?.date_start).format('MM/DD/YYYY')
                : 'N/A'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={2} mr={2}>
          {isAdding || isEditing ? (
            <FHAntdDateTimePicker
              format="MM/DD/YYYY"
              inputHeight={40}
              sx={{
                width: '100%',
                boxShadow: 'none',
              }}
              coloredBackground
              allowClear={true}
              value={
                editableAssignment.date_end
                  ? moment(editableAssignment.date_end, 'YYYY-MM-DD')
                  : null
              }
              onSelect={(value: Moment) =>
                handleFieldChange('date_end', value.format('YYYY-MM-DD'))
              }
              onChange={(value: Moment | null) =>
                handleFieldChange('date_end', value ? value.format('YYYY-MM-DD') : null)
              }
            />
          ) : (
            <Typography>
              {clientAssignment?.date_end
                ? moment(clientAssignment?.date_end).format('MM/DD/YYYY')
                : 'N/A'}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {
            <>
              {isAdding || isEditing ? (
                <Box>
                  <FHButton disabled={isDisabled} onClick={handleSave} sx={{ mr: 4 }}>
                    {isAdding ? 'Save' : 'Update'}
                  </FHButton>
                  <FHButton
                    sx={{
                      border: `2px solid ${FORTA_BLUE.primary}`,
                    }}
                    lightBackground={true}
                    onClick={handleCancel}
                  >
                    Cancel
                  </FHButton>
                </Box>
              ) : showActions ? (
                <Box>
                  <FHButton
                    sx={{
                      border: `2px solid ${FORTA_BLUE.primary}`,
                      mr: 4.5,
                    }}
                    disabled={isDisabled}
                    onClick={onEdit}
                  >
                    Edit
                  </FHButton>
                  <FHButton
                    sx={{
                      border: `2px solid ${FORTA_BLUE.primary}`,
                    }}
                    disabled={isDisabled}
                    lightBackground={true}
                    onClick={handleDelete}
                  >
                    Delete
                  </FHButton>
                </Box>
              ) : null}
            </>
          }
        </Grid>
      </Grid>
    </>
  );
};

export default CohortAssignmentRow;
